import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { userSettingsLabels } from '../UserSettingsForm/UserSettings';
import styles from './UserSettingsAndAccessForm.module.css';
import SettingsGroup, { visibleSettingKeys } from './SettingsGroup';
import AccessCheckbox from './AccessCheckbox';
import type { PermissionMap, UserSettings, UserOrganizationSettings, UserData } from 'admin/types';
import { BlockedByPersonaText } from 'admin/utils/shouldRender';
import { PermissionKey } from 'js-sdk/src/types/Persona';
import { mobxInjectSelect } from 'common/utils';
import { DotsIndicator } from 'common/components';
import { useAppSelector } from 'redux-stores';
import TCClient from 'client';

type MobxProps = {
  featureFlags: Record<string, unknown>;
  getURLParams: () => Record<string, string>;
  isPersonaMessageTtlAndPinLockEnabled: boolean;
  isPmaPatientEngagementEnabled: boolean;
  loadPermissions: (
    selectedOrganizationId: string
  ) => Promise<{ name: PermissionKey; description: string }[]>;
};

type UserSettingsAndAccessFormProps = {
  disableAll?: boolean;
  isPersonaForm?: boolean;
  userPermissions: PermissionMap | undefined;
  userSettings: UserSettings;
  setUserSettings: React.Dispatch<React.SetStateAction<UserSettings | undefined>>;
  setUserPermissions: React.Dispatch<React.SetStateAction<PermissionMap | undefined>>;
  userData?: UserData;
};

function PersonasSettingsAndAccessForm({
  disableAll = false,
  featureFlags,
  isPersonaForm = false,
  isPersonaMessageTtlAndPinLockEnabled,
  isPmaPatientEngagementEnabled,
  getURLParams,
  loadPermissions,
  userPermissions = {},
  userSettings,
  setUserSettings,
  setUserPermissions,
}: UserSettingsAndAccessFormProps & MobxProps) {
  const [userOrgSettings, setUserOrgSettings] = useState<UserOrganizationSettings>();
  const [orgPermissions, setOrgPermissions] = useState<
    Map<PermissionKey, { name: string; description: string }>
  >(new Map());
  const [permissions, setPermissions] = useState<PermissionMap>({});
  // Patient engagement values from before Patient Messaging Admin was checked, restored on uncheck.
  const prePatientMessagingAdminValues = useRef<Partial<UserSettings>>({});
  const organizationId = useAppSelector((state) => state.admin.selectedOrganizationId);
  const urlParams = getURLParams();

  useEffect(() => {
    TCClient.adminUsers
      .fetchOrganizationSettings({ organizationId })
      .then((org_settings) => setUserOrgSettings(org_settings));
    loadPermissions(organizationId).then((permissions) =>
      setOrgPermissions(new Map(permissions.map((p) => [p.name, p])))
    );
  }, [loadPermissions, organizationId]);

  useEffect(() => {
    const reduceRes = Array.from(orgPermissions.keys()).reduce(
      (pMap, permission) => ({
        ...pMap,
        [permission]: userPermissions?.[permission] || false,
      }),
      {}
    );
    setPermissions(reduceRes);
  }, [isPersonaForm, orgPermissions, userPermissions]);

  if (!userSettings || !userOrgSettings) {
    return (
      <div className={classNames(styles.loadingHolder)}>
        <DotsIndicator color="#0e0d0d" size={12} marginTop={30} marginRight={5} />
      </div>
    );
  }

  const { settings, patientEngagementSettings } = userSettingsLabels(
    userSettings,
    userOrgSettings,
    setUserSettings,
    featureFlags,
    urlParams,
    disableAll,
    'v2',
    () => false,
    isPersonaForm
  );

  const handlePermissionClick = (permission: string, checked: boolean) => {
    setUserPermissions((prev) => ({ ...prev, [permission]: checked }));
    if (!isPmaPatientEngagementEnabled || permission !== 'patient_messaging_admin') return;

    // Patient Messaging Admin checks every visible patient engagement setting, and unchecking it
    // restores them. Visibility is evaluated with patient engagement on, so its dependent settings
    // are included.
    const { patientEngagementSettings: visibleSettings } = userSettingsLabels(
      { ...userSettings, patientEngagementSetting: true },
      userOrgSettings,
      setUserSettings,
      featureFlags,
      urlParams,
      disableAll,
      'v2',
      () => false,
      isPersonaForm
    );
    const keys = visibleSettingKeys(visibleSettings, userOrgSettings, isPersonaForm);
    if (checked) {
      keys.forEach((key) => {
        if (!(key in prePatientMessagingAdminValues.current)) {
          prePatientMessagingAdminValues.current = {
            ...prePatientMessagingAdminValues.current,
            [key]: userSettings[key],
          };
        }
      });
      setUserSettings((prev) =>
        prev ? { ...prev, ...Object.fromEntries(keys.map((key) => [key, true])) } : prev
      );
      return;
    }
    // Settings with nothing remembered (the persona was opened with Patient Messaging Admin on)
    // are turned off.
    const previousValues = prePatientMessagingAdminValues.current;
    prePatientMessagingAdminValues.current = {};
    setUserSettings((prev) =>
      prev
        ? { ...prev, ...Object.fromEntries(keys.map((key) => [key, false])), ...previousValues }
        : prev
    );
  };

  const settingsToRemove = isPersonaMessageTtlAndPinLockEnabled
    ? []
    : ['Message Lifespan', 'PIN Lock (mobile)'];
  const filteredSettings = settings.filter((setting) => !settingsToRemove.includes(setting.label));

  return (
    <div className={styles.userSettingsAndAccessForm}>
      <hr className={classNames(styles.personaHr)} />
      <div className={styles.settingsGroup}>
        <SettingsGroup
          isPersonaForm={isPersonaForm}
          title="General Settings"
          settings={filteredSettings}
          userOrgSettings={(userOrgSettings || {}) as UserOrganizationSettings}
        />
        <SettingsGroup
          isPersonaForm={isPersonaForm}
          title="Patient Engagement Settings"
          settings={patientEngagementSettings}
          userOrgSettings={(userOrgSettings || {}) as UserOrganizationSettings}
        />
      </div>
      <hr className={classNames(styles.personaHr)} />
      <div className={styles.settingsGroup}>
        <div className={styles.settingsGroupTitle}>Access</div>
        <div className={styles.settingsGroupGrid}>
          {Object.keys(permissions).map((permission) => {
            return (
              <AccessCheckbox
                key={permission}
                isLockedByPersona={false}
                permission={permission}
                blockedByPersonaText={BlockedByPersonaText}
                orgPermissions={orgPermissions}
                disableAll={disableAll}
                checked={permissions[permission as keyof PermissionMap]}
                onClick={(checked: boolean) => handlePermissionClick(permission, checked)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default mobxInjectSelect<UserSettingsAndAccessFormProps, MobxProps>({
  featureStore: ['featureFlags', 'getURLParams'],
  messengerStore: ['isPersonaMessageTtlAndPinLockEnabled', 'isPmaPatientEngagementEnabled'],
  personaStore: ['loadPermissions'],
})(PersonasSettingsAndAccessForm);
